import { useEffect, useMemo } from 'react';

import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';

import { baseQueryArgs } from './constants';
import { GqlQuery } from './types';

function defaultParser<TValue = string>(value?: unknown): TValue {
  if (!(value as any)?.value && process.env.NODE_ENV !== 'production') {
    /* istanbul ignore next */

    console.warn(
      'Unable to process fetchItemQuery result in useSelectedItem.  Please check the `parseResponse` parameter.',
    );
  }

  return (value as any)?.value as TValue;
}

export interface SelectedItemProps<TValue = string> {
  query: GqlQuery;
  /** Get the item from the `fetchItemQuery` response */
  parseResponse?: (resp: unknown) => TValue;

  /** Current value of the component */
  id?: number;
  skip?: boolean;
}

export function useSelectedItem<TValue = string>({
  query,
  parseResponse = defaultParser,
  id,
  skip = false,
}: SelectedItemProps<TValue>) {
  const { fetch, data, isLoading, error, ...details } = useLazyGraphql(query, baseQueryArgs);

  useEffect(() => {
    if (id && !skip) {
      fetch({ id });
    }
  }, [id]);

  const result = useMemo(() => parseResponse(data), [data]);

  return {
    result: id ? result : undefined,
    isLoading,
    error,
    ...details,
  };
}
